import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { useState } from "react";
import {
  CreditCard,
  PackageCheck,
  Activity,
  Unlock,
  Sparkles,
  Tag,
  PhoneCall,
} from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import Accordion from "@/components/Accordion";

const MACHINE_IMG =
  "/media/vending-machine-orange-wall.png";

import { Helmet } from "react-helmet-async";

const REGIONS = {
  AU: {
    label: "Country-wide",
    powerCost: "~$100 / year",
    machineWidth: "1056mm",
    testimonials: [
      { quote: "Easy install, no fuss. Great for busy lunch rooms where people want quick options.", attr: "Site Supervisor, Workshop" },
      { quote: "Stock levels are always topped up. We don't think about it anymore.", attr: "Facilities Lead, Distribution Centre" },
      { quote: "Tap & go made a massive difference. The service is reliable and the product mix is exactly what we asked for.", attr: "Office Admin, Corporate Site" },
    ],
  },
  UK: {
    label: "Country-wide · UK",
    powerCost: "~£60 / year",
    machineWidth: "1056mm",
    testimonials: [
      { quote: "Tap & go made a massive difference. The service is reliable and the product mix is exactly what we asked for.", attr: "Office Admin, Corporate Site" },
      { quote: "Brilliant kit, brilliant service. Staff actually use it daily.", attr: "Operations Manager, London Warehouse" },
      { quote: "Restocks happen before we even notice we're low. Top tier.", attr: "Site Lead, Manchester Office" },
    ],
  },
  US: {
    label: "Country-wide · USA",
    powerCost: "~$110 / year",
    machineWidth: "41 in (1056 mm)",
    testimonials: [
      { quote: "Crew loves it. Tap-to-pay means the line never stalls during shift change.", attr: "Plant Manager, Midwest Facility" },
      { quote: "Healthier options actually move. Custom mix was the right call.", attr: "HR Director, Tech Office" },
      { quote: "Zero upfront, full service. It just works.", attr: "Operations Lead, Distribution Center" },
    ],
  },
};

const FAQS = [
  {
    q: "Do we have to buy the machine?",
    a: "No. Most qualifying workplaces don't buy at all — we install a machine for $0 upfront. We can also discuss outright purchase if it suits your site better.",
  },
  {
    q: "What staff numbers do you need?",
    a: "Typically 30–80+ staff is ideal, but consistent foot traffic (visitors, drivers, customers, members) can also qualify smaller sites.",
  },
  {
    q: "Can we choose what goes in it?",
    a: "Yes. You pick the mix — snacks, drinks, energy, healthier options like kombucha and protein bars. The mix can be tuned over time based on usage.",
  },
  {
    q: "What if nobody uses it?",
    a: "We'll discuss adjustments first (placement, mix, pricing). If usage stays low, the machine can be removed with no penalties — no lock-in.",
  },
];

export default function BuyVendingMachine({ region = "AU", testId = "buy-page-au" }) {
  const cfg = REGIONS[region];

  return (
    <div data-testid={testId} className="text-white">
      <Seo />
      <HeroSection cfg={cfg} />
      <WhatYouGetSection />
      <BestFitBanner />
      <HowItWorksSection />
      <SpecsAndCategoriesSection cfg={cfg} />
      <TestimonialsSection items={cfg.testimonials} />
      <FAQSection />
      <FormSection />
    </div>
  );
}

function HeroSection({ cfg }) {
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <div className="absolute inset-0 bg-radial-fade" />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
            Anytime Vending Machines · {cfg.label}
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-balance">
            Buy a Vending Machine
            <span className="block text-zinc-400">— or get one installed{" "}
              <span className="text-[#FE7200]">free</span>
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Modern vending machines for workplaces with <b className="text-white">30+ staff</b> or steady foot traffic. We deliver, install, stock, service and monitor — you get the convenience at <b className="text-white">zero upfront cost</b>.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#book-form"
              data-testid="buy-hero-book"
              className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book Free Site Visit
            </a>
            <a
              href="#how-it-works"
              data-testid="buy-hero-how"
              className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
            >
              How It Works
            </a>
          </div>
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
            {["No lock-in contracts", "Tap & Go + cash/coin", "4-week trial option"].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
            <img src={MACHINE_IMG} alt="Modern vending machine" className="w-full h-72 sm:h-96 object-cover opacity-95" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-sm sm:text-base">
              <span className="font-semibold text-white">Modern machines</span>
              <span className="text-zinc-300"> · Tap &amp; Go · Fully stocked &amp; serviced by us.</span>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 shadow-[0_30px_60px_-20px_rgba(254,114,0,0.2)]">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">Quick Eligibility Check</div>
                <h3 className="mt-1 font-display text-lg font-bold text-white">Takes 30 seconds.</h3>
              </div>
            </div>
            <p className="mt-2 text-sm text-zinc-400">If you qualify, we'll organise a site visit.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <a
                href="#book-form"
                data-testid="buy-go-to-form"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-black hover:bg-[#FF8C2E]"
              >
                Go To Form
              </a>
              <a
                href="#how-it-works"
                data-testid="buy-how-it-works"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-white/10"
              >
                How It Works
              </a>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3">
              <div className="rounded-lg border border-white/10 bg-[#0f0f10] px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Popular Machine Size</div>
                <div className="mt-1 text-sm text-white font-semibold">1830mm H · {cfg.machineWidth} W · 830mm D</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0f0f10] px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Estimated Power Usage</div>
                <div className="mt-1 text-sm text-white font-semibold">{cfg.powerCost}</div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function WhatYouGetSection() {
  const items = [
    { icon: CreditCard, title: "Modern payments", text: "Tap & Go / PayPass, plus cash and coins — easy for everyone on site." },
    { icon: PackageCheck, title: "Fully stocked & serviced", text: "We restock and maintain the machine so it's always clean, full, and reliable." },
    { icon: Activity, title: "Back-to-base monitoring", text: "Stock levels are monitored so we know when it's time to refill." },
    { icon: Unlock, title: "No lock-in", text: "If it's not being used, we can collect it — no drama." },
    { icon: Sparkles, title: "Custom product mix", text: "Snacks, drinks, food and healthier options — tailored to your team." },
    { icon: Tag, title: "Zero upfront cost*", text: "Qualifying sites can receive a machine installed with no upfront fees." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="buy-what-you-get">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
        What you get with Anytime Vending Machines
      </h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">A professional vending setup that actually gets used — and stays stocked.</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, i) => (
          <div
            key={it.title}
            data-testid={`buy-feature-card-${i}`}
            className="group rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-[#FE7200]/40 hover:-translate-y-1 transition-all"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FE7200]/15 border border-[#FE7200]/30 text-[#FE7200]">
              <it.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-white">{it.title}</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{it.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function BestFitBanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12" data-testid="buy-best-fit">
      <div className="rounded-2xl border border-[#FE7200]/30 bg-gradient-to-r from-[#FE7200]/15 to-transparent p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl md:text-2xl font-bold text-white">
            Best fit: workplaces with 30–80+ staff
          </div>
          <div className="text-sm text-zinc-300 mt-1">
            If you're below that, still ask — foot traffic and site type matter too.
          </div>
        </div>
        <a
          href="#book-form"
          data-testid="buy-best-fit-cta"
          className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
        >
          Book Free Site Visit
        </a>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { n: 1, title: "Site visit", text: "We come out, check access and placement, and confirm requirements." },
    { n: 2, title: "Choose products", text: "Pick your snacks/drinks/healthy options. We build the mix with you." },
    { n: 3, title: "Install", text: "We deliver, test, and install the machine — ready to go immediately." },
    { n: 4, title: "Ongoing service", text: "Regular restocking and fast maintenance response when needed." },
  ];
  return (
    <section id="how-it-works" className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="buy-how-it-works">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">How it works</h2>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {steps.map((s, i) => (
          <div
            key={s.n}
            data-testid={`buy-step-${i}`}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 flex gap-5"
          >
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#FE7200] text-black font-display font-extrabold flex items-center justify-center text-lg">
              {s.n}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
              <p className="text-sm text-zinc-400 mt-1">{s.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SpecsAndCategoriesSection({ cfg }) {
  const chips = ["Water", "Soft drinks", "Energy drinks", "Chips", "Chocolate", "Protein bars", "Healthy snacks", "Food options"];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="buy-specs-categories">
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7" data-testid="buy-machine-specs">
        <h3 className="font-display text-xl font-bold text-white">Machine specs</h3>
        <ul className="mt-4 space-y-2 text-sm text-zinc-300">
          <li><b className="text-white">Height:</b> 1830mm</li>
          <li><b className="text-white">Width:</b> {cfg.machineWidth}</li>
          <li><b className="text-white">Depth:</b> 830mm</li>
          <li><b className="text-white">Power:</b> approx. {cfg.powerCost}</li>
          <li><b className="text-white">Payments:</b> Tap &amp; Go, PayPass, cash &amp; coin</li>
        </ul>
        <p className="mt-3 text-xs text-zinc-500">Standard power point required. We'll confirm access during site visit.</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7" data-testid="buy-categories">
        <h3 className="font-display text-xl font-bold text-white">Common product categories</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} className="inline-flex items-center rounded-full border border-white/10 bg-[#0f0f10] px-3 py-1.5 text-xs text-zinc-200">
              {c}
            </span>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-[#FE7200]/40 bg-[#FE7200]/10 p-4 text-sm text-[#FFB870]">
          Want "no junk food"? No worries — we can stock kombucha, nut bars, waters, and more.
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ items }) {
  const [idx, setIdx] = useState(0);
  return (
    <section className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="buy-testimonials">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What sites love</h2>
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-8" data-testid={`testimonial-${idx}`}>
        <p className="font-display text-lg md:text-xl text-white leading-relaxed">"{items[idx].quote}"</p>
        <div className="mt-4 text-sm text-zinc-400">— {items[idx].attr}</div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-2">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            data-testid={`testimonial-dot-${i}`}
            aria-label={`Show testimonial ${i + 1}`}
            className={`h-2.5 rounded-full transition-all ${i === idx ? "bg-[#FE7200] w-8" : "bg-white/20 w-2.5 hover:bg-white/40"}`}
          />
        ))}
      </div>
    </section>
  );
}

function FAQSection() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <section className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="buy-faq">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">FAQ</h2>
      <p className="mt-3 text-zinc-300">The questions we hear most often.</p>
      <div className="mt-8">
        <Accordion items={FAQS} testIdPrefix="faq" defaultOpen={0} />
      </div>
    </section>
  );
}

function FormSection() {
  return (
    <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-10 md:py-16 border-t border-white/5" data-testid="buy-form-section">
      <SiteVisitForm source="buy-vending-machine" testIdPrefix="buy" />
    </section>
  );
}
